/**
 * CompanyLogoPanel — Settings → Company logo (task #58).
 *
 * The company's colour logo, shown on invoices, customer emails and at the top of the menu.
 * It is separate from the black-and-white logo printed on tickets (Printed tickets). Rendered
 * inside the TENANT_ADMIN-gated section; the API is MANAGE_TENANT.
 *
 * Two previews: the logo itself on white (as it heads an invoice), and the menu header drawn
 * by the real TenantBrand from GET /branding, so it shows the server's one-colour treatment.
 * With no logo, both show what people see instead: the company name.
 */

import * as React from 'react';
import { apiErrorText, TenantBrand, useTenantBrand } from '@lodex-pro/web-shared';
import { useCompanyLogo, useUploadCompanyLogo, useDeleteCompanyLogo } from './use-company-logo';

const panelStyle: React.CSSProperties = {
  border: '1px solid var(--color-border-primary)',
  borderRadius: '10px',
  padding: '20px',
  backgroundColor: 'var(--color-bg-secondary, var(--color-bg-primary))',
  maxWidth: '620px',
};
const titleStyle: React.CSSProperties = {
  margin: '0 0 4px',
  fontSize: '18px',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
};
const descStyle: React.CSSProperties = {
  margin: '0 0 20px',
  fontSize: '13px',
  color: 'var(--color-text-secondary)',
};
const labelStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
};
const helperStyle: React.CSSProperties = { fontSize: '12px', color: 'var(--color-text-tertiary)' };
const previewRow: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px',
  alignItems: 'flex-start',
};
const previewCol: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '6px' };
/** White in both themes on purpose: it is the paper the invoice is printed on. */
const paperStyle: React.CSSProperties = {
  width: '300px',
  height: '140px',
  boxSizing: 'border-box',
  padding: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#ffffff',
  border: '1px solid var(--color-border-primary)',
  borderRadius: '8px',
  color: '#111827',
  fontWeight: 700,
  fontSize: '16px',
  textAlign: 'center',
};
/** The menu's own look (AppShell): same width less its padding, same background. */
const menuStyle: React.CSSProperties = {
  width: '240px',
  boxSizing: 'border-box',
  padding: '16px',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-card)',
};
const actionRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginTop: '16px',
};
const primaryBtn: React.CSSProperties = {
  height: '38px',
  padding: '0 18px',
  fontWeight: 600,
  fontSize: '14px',
  borderRadius: 'var(--radius-pill)',
  border: 'none',
  cursor: 'pointer',
  backgroundColor: 'var(--color-accent-blue)',
  color: 'var(--color-on-accent)',
};
const secondaryBtn: React.CSSProperties = {
  ...primaryBtn,
  backgroundColor: 'transparent',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-border-primary)',
};
const dangerBtn: React.CSSProperties = {
  ...secondaryBtn,
  color: 'var(--color-danger)',
  borderColor: 'var(--color-danger)',
};
const errText: React.CSSProperties = { fontSize: '13px', color: 'var(--color-danger)' };

export function CompanyLogoPanel(): React.JSX.Element {
  const { data, isLoading } = useCompanyLogo();
  const brand = useTenantBrand();
  const upload = useUploadCompanyLogo();
  const remove = useDeleteCompanyLogo();
  const fileRef = React.useRef<HTMLInputElement | null>(null);

  const logo = data?.logo ?? null;
  const name = brand.tenantName ?? '';

  const onPickFile = (): void => fileRef.current?.click();
  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file !== undefined) {
      remove.reset();
      upload.mutate(file);
    }
    e.target.value = ''; // allow re-picking the same file
  };
  const onRemove = (): void => {
    if (
      !window.confirm(
        'Remove the company logo? Invoices, customer emails and the menu will show your company name instead.',
      )
    ) {
      return;
    }
    upload.reset();
    remove.mutate();
  };

  return (
    <section style={panelStyle} aria-label="Company logo">
      <h2 style={titleStyle}>Company logo</h2>
      <p style={descStyle}>
        Your logo in colour, on invoices, customer emails and at the top of the menu. The
        black-and-white logo printed on tickets is set separately, under Printed tickets.
      </p>

      <div style={previewRow}>
        <div style={previewCol}>
          <span style={labelStyle}>On invoices and emails</span>
          <div style={paperStyle} data-testid="company-logo-paper">
            {logo !== null ? (
              <img
                src={logo.dataUrl}
                alt={name !== '' ? `${name} logo` : 'Company logo'}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            ) : (
              <span>{name}</span>
            )}
          </div>
        </div>
        <div style={previewCol}>
          <span style={labelStyle}>In the menu</span>
          <div style={menuStyle} data-testid="company-logo-menu">
            <TenantBrand tenantName={brand.tenantName} logo={brand.logo} />
          </div>
        </div>
      </div>

      <p style={{ ...helperStyle, margin: '10px 0 0' }}>
        {isLoading
          ? 'Loading…'
          : logo !== null
            ? `Current logo: ${String(logo.width)} × ${String(logo.height)} px.`
            : 'No company logo yet, so your company name is shown instead.'}
      </p>

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg"
        onChange={onFileChange}
        style={{ display: 'none' }}
        data-testid="company-logo-input"
      />
      <div style={actionRow}>
        <button
          type="button"
          onClick={onPickFile}
          disabled={upload.isPending || isLoading}
          style={logo !== null ? secondaryBtn : primaryBtn}
        >
          {upload.isPending ? 'Uploading…' : logo !== null ? 'Replace logo' : 'Upload logo'}
        </button>
        {logo !== null && (
          <button type="button" onClick={onRemove} disabled={remove.isPending} style={dangerBtn}>
            {remove.isPending ? 'Removing…' : 'Remove logo'}
          </button>
        )}
      </div>
      <p style={{ ...helperStyle, margin: '8px 0 0' }}>
        PNG or JPG, up to 2 MB. A transparent background looks best. Larger images are reduced to
        1024 px.
      </p>
      {upload.isError && (
        <p role="alert" style={{ ...errText, margin: '8px 0 0' }}>
          {apiErrorText(upload.error, 'Could not upload the logo. Try again.')}
        </p>
      )}
      {remove.isError && (
        <p role="alert" style={{ ...errText, margin: '8px 0 0' }}>
          Remove failed. Try again.
        </p>
      )}
    </section>
  );
}
